import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteBoard, type DeleteBoardRequest } from '@/features/class/boardsApi';
import { useAuthStore, type RawAuthUser } from '@/store/authStore';

/**
 * Remove a deleted board from the auth-store's `rawUser` payload so the
 * Sidebar/profile (which read owned classes from the login response, not
 * React Query) drop the class immediately without a full page refresh.
 *
 * Mirrors `injectCreatedBoardIntoRawUser` in `useCreateBoard.ts`: it keeps
 * both the legacy flat `board[]` and the modern `boardWithCategory[]` shapes
 * consistent.
 */
function removeBoardFromRawUser(boardId: number | string): void {
  const { rawUser, setRawUser } = useAuthStore.getState();
  if (!rawUser) return;

  const targetId = String(boardId);
  const base: RawAuthUser = { ...rawUser };

  const matchesTarget = (entry: unknown): boolean =>
    Boolean(
      entry &&
      typeof entry === 'object' &&
      String((entry as { id?: number | string }).id) === targetId,
    );

  // 1) Legacy flat `board[]`.
  if (Array.isArray(base.board)) {
    base.board = (base.board as unknown[]).filter((entry) => !matchesTarget(entry));
  }

  // 2) Modern `boardWithCategory[]` — drop the row from every group.
  if (Array.isArray(base.boardWithCategory)) {
    base.boardWithCategory = (base.boardWithCategory as unknown[]).map((group) => {
      if (
        group &&
        typeof group === 'object' &&
        Array.isArray((group as Record<string, unknown>).boards)
      ) {
        const typedGroup = group as Record<string, unknown>;
        return {
          ...typedGroup,
          boards: (typedGroup.boards as unknown[]).filter((entry) => !matchesTarget(entry)),
        };
      }
      return group;
    });
  }

  setRawUser(base);
}

/**
 * DELETE /midl/api/v3/board — permanently deletes a class (board).
 *
 * Wired from `ClassFormModal` so the "Delete class" action persists to the
 * real backend. On success we also patch the auth-store `rawUser` so the
 * sidebar/profile (login-payload driven) and the React-Query joined-boards
 * cache all drop the class without a hard reload.
 */
export function useDeleteBoardMutation() {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, DeleteBoardRequest>({
    mutationKey: ['class', 'board', 'delete'],
    mutationFn: (vars) => deleteBoard(vars),
    onSuccess: (_data, vars) => {
      removeBoardFromRawUser(vars.boardId);
      // Refresh server-driven sidebar/class lists too.
      void queryClient.invalidateQueries({ queryKey: ['boards', 'joined'] });
    },
  });
}
